export interface EventMap {
  'scenario:start': { id: string; title: string; hudLabel?: string; telemetry?: string };
  'scenario:end': Record<string, never>;
  'hud:event': { icon: string; title: string; detail: string; severity: string };
  'hud:region': { regions: Array<{ name: string; status: string; properties: number }> };
  'hud:team': { teams: Array<{ name: string; status: string; eta?: string; destination: string }> };
  'hud:weather': { windSpeed: number; rainfall: number; pressure: number };
  'notification': { title: string; message: string; severity?: string };
  'screen:show': { image?: string; component?: string; device: 'desktop' | 'mobile'; title: string; subtitle?: string; role?: string; url?: string; duration: number };
  'screen:hide': Record<string, never>;
  'scenario:state': { active: boolean; paused: boolean };
  'timeline:command': { action: 'pause' | 'resume' | 'toggle' | 'stop' };
  'hud:metric': {
    id: string;
    kind: 'countdown' | 'counter' | 'burndown' | 'rag-legend';
    label: string;
    value: number;
    target?: number;
    unit?: string;
    severity?: string;
    durationMs?: number;
  };
  'hud:metric-clear': { id: string };
  'color-grade': { tint?: string; intensity?: number; vignette?: number };
}

type Handler<T> = (data: T) => void;

export class EventBus {
  private listeners = new Map<string, Set<Handler<any>>>();

  on<K extends keyof EventMap>(event: K, handler: Handler<EventMap[K]>): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler);
    return () => {
      this.listeners.get(event)?.delete(handler);
    };
  }

  emit<K extends keyof EventMap>(event: K, data: EventMap[K]): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      for (const handler of handlers) {
        handler(data);
      }
    }
  }

  clear(): void {
    this.listeners.clear();
  }
}

export const eventBus = new EventBus();
